/**
 * The v1 realtime envelope: the SNS message body every producer publishes,
 * forwarded unchanged to the socket.
 *
 * `type` is what the client dispatches on, so a new kind of event is a new
 * handler here and nothing else - the gateway, the broadcaster and the ticket
 * are all type-blind. Events are hints carrying the authoritative value; REST
 * stays the source of truth, which is why every (re)connect refetches.
 */
export interface RealtimeEnvelope<T = unknown> {
  v: 1;
  type: string;
  /** null for a device with no organization, which only superusers receive. */
  org: string | null;
  /** When it happened at the source (the shadow timestamp), in ms. */
  occurred_at: number;
  subject: Record<string, string>;
  data: T;
}

export interface DeviceStateData {
  state: string;
  previous_state?: string | null;
  /** The derived online/offline status, kept for what still reads it. */
  status: string;
  /** The shadow version: monotonic per device, so it orders events. */
  version: number;
}

export type DeviceStateEnvelope = RealtimeEnvelope<DeviceStateData> & {
  type: "device.state";
  subject: { device: string };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * A socket frame as an envelope, or undefined for anything that is not one -
 * a reply to our own ping, a version this build does not speak, junk.
 */
export const parseEnvelope = (raw: unknown): RealtimeEnvelope | undefined => {
  if (typeof raw !== "string") return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    return undefined;
  }
  if (
    !isObject(parsed) ||
    parsed.v !== 1 ||
    typeof parsed.type !== "string" ||
    !isObject(parsed.subject)
  ) {
    return undefined;
  }
  return parsed as unknown as RealtimeEnvelope;
};

export const isDeviceStateEnvelope = (
  envelope: RealtimeEnvelope,
): envelope is DeviceStateEnvelope => {
  const data = envelope.data as Partial<DeviceStateData> | undefined;
  return (
    envelope.type === "device.state" &&
    typeof envelope.subject.device === "string" &&
    !!envelope.subject.device &&
    typeof envelope.occurred_at === "number" &&
    isObject(data) &&
    typeof data.state === "string" &&
    typeof data.status === "string" &&
    typeof data.version === "number"
  );
};
